import "./CreateNotification.css";

import { Breadcrumb, Button, Form, Input, message, Select, Switch } from "antd";
import { useEffect } from "react";
import { BsTrash } from "react-icons/bs";
import { CgScreen } from "react-icons/cg";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  createNotification,
  deleteNotification,
  getNotifications,
  type NotificationFormType,
  updateNotification,
  useAppSelector,
} from "../../../_lib/_store";

const { TextArea } = Input;

interface CreateNotificationPageProps {
  type: "create" | "edit";
}

export const CreateNotificationPage = ({
  type,
}: CreateNotificationPageProps) => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const { isLoading } = useAppSelector((state) => state.notification);
  const navigate = useNavigate();
  const { state } = useLocation();

  const isEdit = type === "edit";

  useEffect(() => {
    const notificationObj = state?.notification;
    if (isEdit && !notificationObj) {
      navigate("/notification");
    }
    if (isEdit) {
      form.setFieldsValue(notificationObj);
    }
    getData();
  }, [state]);

  const getData = async () => {
    await getNotifications(messageApi);
  };

  const handleSubmit = async (values: NotificationFormType) => {
    const payload = {
      ...values,
      ...(isEdit ? { id: state.notification.id } : {}),
    };

    if (isEdit) {
      const res = await updateNotification(messageApi, payload);
      if (res) {
        navigate("/notification/edit", {
          state: { notification: res },
          replace: true,
        });
      }
    } else {
      const res = await createNotification(messageApi, payload);
      if (res) {
        form.resetFields();
      }
    }
  };

  const handleDelete = async () => {
    const res = await deleteNotification(messageApi, state.notification.id);
    if (res) {
      navigate("/notification");
    }
  };

  return (
    <>
      {contextHolder}
      <div className="createPackage">
        <Breadcrumb
          items={[
            {
              title: <Link to="/dashboard">Dashboard</Link>,
            },
            {
              title: <Link to="/notification">Notification</Link>,
            },
            {
              title: isEdit ? "Edit" : "Create new",
            },
          ]}
        />

        {!isEdit ? (
          <div>
            <h2 className="title">Create New Notification</h2>
            <p style={{ color: "#999", marginTop: "8px" }}>
              Send important updates and announcements to your users
            </p>
          </div>
        ) : (
          <div className="titleHeader">
            <h2 className="title">Edit Notification</h2>
            <div className="actionButton">
              <Button
                icon={<CgScreen size={17} />}
                size="large"
                onClick={() =>
                  navigate(`/notification/show`, {
                    state: { notification: state?.notification },
                  })
                }
              >
                Show
              </Button>
              <Button
                icon={<BsTrash />}
                size="large"
                onClick={handleDelete}
                loading={isLoading}
              >
                Delete
              </Button>
            </div>
          </div>
        )}

        <div className="form">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              type: "Information",
              targetAudience: "All Users",
              sendPushNotification: false,
              sendEmailNotification: false,
            }}
          >
            <Form.Item
              label="Notification Title"
              name="title"
              rules={[
                { required: true, message: "Notification title is required" },
              ]}
            >
              <Input size="large" placeholder="Enter notification title..." />
            </Form.Item>

            <Form.Item
              label="Message"
              name="message"
              rules={[{ required: true, message: "Message is required" }]}
            >
              <TextArea
                size="large"
                placeholder="Enter your notification message..."
                rows={4}
              />
            </Form.Item>

            <div style={{ display: "flex", gap: "16px" }}>
              <Form.Item
                label="Notification Type"
                name="type"
                style={{ flex: 1 }}
                rules={[
                  { required: true, message: "Notification type is required" },
                ]}
              >
                <Select
                  size="large"
                  options={[
                    { label: "Information", value: "Information" },
                    { label: "Warning", value: "Warning" },
                    { label: "Success", value: "Success" },
                    { label: "Error", value: "Error" },
                  ]}
                />
              </Form.Item>

              <Form.Item
                label="Target Audience"
                name="targetAudience"
                style={{ flex: 1 }}
                rules={[
                  { required: true, message: "Target audience is required" },
                ]}
              >
                <Select
                  size="large"
                  options={[
                    { label: "All Users", value: "All Users" },
                    { label: "Administrators", value: "Administrators" },
                    { label: "Premium Users", value: "Premium Users" },
                    { label: "Beta Testers", value: "Beta Testers" },
                  ]}
                />
              </Form.Item>
            </div>

            <Form.Item name="sendPushNotification" valuePropName="checked">
              <Switch />{" "}
              <span style={{ marginLeft: 8 }}>Send push notification</span>
            </Form.Item>

            <Form.Item name="sendEmailNotification" valuePropName="checked">
              <Switch />{" "}
              <span style={{ marginLeft: 8 }}>Send email notification</span>
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={isLoading}
              >
                Send Notification
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </>
  );
};
